<template>
  <figure class="base-table">
    <slot class="figcaption" name="figcaption" />
    <div class="base-table-wrapper">
      <table
        role="table"
        :class="[
          size && `size-${size}`,
        ]"
      >
        <slot name="tablecatpion" />
        <slot />
      </table>
    </div>
  </figure>
</template>

<script lang="ts" setup>
interface Props {
  size?: 'full'
}

defineProps<Props>()
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.base-table {
  table {
    border-spacing: 0;
    border-collapse: collapse;
    width: fit-content;

    &.size-full {
      width: 100%;
    }
  }
  :deep() {
    figcaption {
      margin-bottom: 8px;
    }
  }
  &.break-all {
    :deep() {
      td,
      th {
        word-break: break-all;
      }
    }
  }

  &.has-border {
    table {
      border-collapse: separate;
    }
    :deep() {
      td,
      th {
        border: none;
        border-right: 1px solid;
        border-bottom: 1px solid;
        border-collapse: separate;
      }
      tr:first-child {
        th,
        td {
          border-top: 1px solid;
          border-collapse: separate;
        }
      }
      thead + tbody {
        tr:first-child {
          th,
          td {
            border-top: none;
          }
        }
      }
      th:first-child,
      td:first-child {
        border-left: 1px solid;
        border-collapse: separate;
      }
    }
  }
  &.has-stripe {
    :deep() {
      tbody {
        tr:nth-child(even) {
          background-color: #fcfcfc;
        }
      }
    }
  }
  &.is-fullwidth {
    :deep() {
      figcaption {
        text-align: center;
      }
    }
    table {
      width: 100%;
      display: table;
    }
  }

  &.is-compact {
    :deep() {
      td,
      th {
        padding: 0.25rem;
      }
    }
  }
  &.is-tight {
    :deep() {
      td,
      th {
        padding: 1px;
      }
    }
  }

  &.is-top-sticky {
    table {
      :deep() {
        th {
          @include is-tablet {
            position: sticky;
            top: 0;
            z-index: 1;
          }
          @include is-print {
            position: static;
          }
        }
      }
    }
  }

  /* theadが無いときのトップのボーダー */
  table:not(:has(thead)) {
    :deep() {
      tbody {
        tr:first-child {
          th,
          td {
            border-top: 1px solid;
            border-collapse: separate;
          }
        }
      }
    }
  }

  :deep() {
    th {
      /* @include font-color(heading);
      @include background-color(grayish); */
      // background-color: #fcfcfc;
    }
    td,
    th {
      border-collapse: separate;
      padding-left: calc(0.25rem * 2);
      padding-right: calc(0.25rem * 2);
      padding-top: calc(0.25rem * 3);
      padding-bottom: calc(0.25rem * 3);
      white-space: pre-wrap;
      border-bottom: 1px solid;
      &.center {
        text-align: center;
      }
      &.left {
        text-align: left;
      }
      &.right {
        text-align: right;
      }
      &.top {
        vertical-align: top;
      }
      &.middle {
        vertical-align: middle;
      }
      &.bottom {
        vertical-align: bottom;
      }
    }
    .h-label {
      display: none;
    }
  }

  /* @include is-mobile() {
    &.responsive-scroll {
      figcaption {
        text-align: left;
      }
      .base-table-wrapper {
        width: 100%;
        overflow-x: scroll;
      }
      table {
        width: max-content;
      }
    }
    &.responsive-sticky {
      .base-table-wrapper {
        width: 100%;
        overflow-x: scroll;
      }
      table {
        width: max-content;

        :deep() {
          th {
            position: sticky;
            left: 0;
            @include is-print() {
              position: static;
            }
          }
        }
      }
    }
    &.responsive-column {
      table {
        display: flex;
        flex-direction: column;
        gap: rem(8);
        :deep() {
          tbody {
            display: flex;
            flex-direction: column;
            gap: rem(8);
          }
          th {
            text-align: left;
          }
          tr {
            display: flex;
            flex-direction: column;
          }
          td,
          th {
            &:empty {
              &::before {
                content: '-';
              }
            }
          }
        }
      }
      &.has-stripe {
        :deep() {
          tbody {
            tr:nth-child(even) {
              background-color: unset;
            }
          }
        }
      }
      &.has-border {
        :deep() {
          tr:first-child {
            th,
            td {
              border: none;
              border-right: 1px solid;
              border-bottom: 1px solid;
              border-left: 1px solid;
              @include color(border-color, separator);
            }
            th:first-child,
            td:first-child {
              border-top: 1px solid;
              @include color(border-color, separator);
            }
          }
          td,
          th {
            border: none;
            border-right: 1px solid;
            border-bottom: 1px solid;
            border-left: 1px solid;
            @include color(border-color, separator);
          }
          th:first-child,
          td:first-child {
            border-top: 1px solid;
            @include color(border-color, separator);
          }
        }
      }
    }
    &.responsive-label {
      table {
        :deep() {
          thead {
            display: none;
            visibility: hidden;
          }
          tbody {
            display: flex;
            flex-direction: column;
            gap: rem(16);
          }
          tr {
            display: flex;
            flex-direction: column;
            .h-label {
              display: block;
            }
            border-top: 1px solid;
            @include border-color(separator);
          }
          td,
          th {
            &:empty {
              display: none;
              visibility: none;
            }
          }
        }
      }
      &.has-border {
        :deep() {
          td,
          th {
            border: none;
            border-right: 1px solid;
            border-bottom: 1px solid;
            border-left: 1px solid;
            @include color(border-color, separator);
          }
        }
      }
      &.has-stripe {
        :deep() {
          tbody {
            tr:nth-child(even) {
              background-color: unset;
            }
            th,
            td {
              &:nth-child(even) {
                background-color: map-get($background-color, accent);
              }
            }
          }
        }
      }
    }
  } */
}
</style>
